import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Icon,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@kognitos/lattice';

import type { IMenuItem } from './types';
import {
  SIDEBAR_DROPDOWN_WIDTH,
  getSidebarDropdownOffset,
} from './sidebar-constants';

// Component to render a menu item
const MenuItem = ({
  item,
  isCollapsed,
  onDropdownOpenChange,
}: {
  item: IMenuItem;
  isCollapsed: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  const handleDropdownOpenChange = (open: boolean) => {
    setDropdownOpen(open);
    onDropdownOpenChange?.(open);
  };

  if (item.children) {
    const isBooleanChildActive = item.children.some(
      child => child.url && location.pathname === child.url,
    );

    if (isCollapsed) {
      return (
        <SidebarMenuItem>
          <DropdownMenu
            open={dropdownOpen}
            onOpenChange={handleDropdownOpenChange}
            modal={false}
          >
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton>
                <Flex
                  align="center"
                  justify="center"
                  className="w-4 h-4 shrink-0"
                >
                  {item.icon && <Icon type={item.icon} className="h-4 w-4" />}
                </Flex>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={SIDEBAR_DROPDOWN_WIDTH}
              sideOffset={getSidebarDropdownOffset(isCollapsed)}
            >
              {item.children.map(child => (
                <DropdownMenuItem
                  key={child.title}
                  className={child.url ? 'p-0' : ''}
                  onClick={() => {
                    handleDropdownOpenChange(false);
                  }}
                >
                  {child.url ? (
                    <NavLink to={child.url} className="w-full px-2 py-1.5">
                      {child.icon && (
                        <Icon type={child.icon} className="mr-2 h-4 w-4" />
                      )}
                      <span>{child.title}</span>
                    </NavLink>
                  ) : (
                    <>
                      {child.icon && (
                        <Icon type={child.icon} className="mr-2 h-4 w-4" />
                      )}
                      <span>{child.title}</span>
                    </>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      );
    }

    // When sidebar is expanded, use collapsible
    return (
      <SidebarMenuItem>
        <Collapsible
          className="group/collapsible"
          defaultOpen={isBooleanChildActive}
        >
          <CollapsibleTrigger asChild>
            <SidebarMenuButton className="p-2 gap-0">
              <Flex
                align="center"
                justify="center"
                className="w-4 h-4 shrink-0"
              >
                {item.icon && <Icon type={item.icon} className="h-4 w-4" />}
              </Flex>
              {!isCollapsed && (
                <>
                  <span className="ml-2 truncate flex-1 min-w-0">
                    {item.title}
                  </span>
                  <Icon
                    type="ChevronRight"
                    className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                  />
                </>
              )}
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children.map(child => (
                <SidebarMenuSubItem key={child.title}>
                  <SidebarMenuSubButton className="p-0" asChild>
                    <div>
                      <NavLink to={child.url!} className="w-full px-2 py-1.5">
                        {child.icon && (
                          <Icon type={child.icon} className="h-4 w-4" />
                        )}
                        <span>{child.title}</span>
                      </NavLink>
                    </div>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton className="gap-0" asChild>
        <div>
          <NavLink to={item.url!} className="w-full py-2">
            <Flex align="center" justify="start">
              {item.icon && (
                <Icon type={item.icon} className="w-4 h-4 shrink-0" />
              )}
              {!isCollapsed && (
                <span className="ml-2 truncate flex-1 min-w-0">
                  {item.title}
                </span>
              )}
            </Flex>
          </NavLink>
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export default MenuItem;
